import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";
import { storeAuthData, clearAuthData } from "@/services/authService";
import { identifyUser, resetUser } from "@/lib/mixpanel";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface ClerkAuthSyncProps {
  children: React.ReactNode;
}

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

export function ClerkAuthSync({ children }: ClerkAuthSyncProps) {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(["common", "auth"]);
  const hasSynced = useRef(false);
  const syncInProgress = useRef(false);
  const retryCount = useRef(0);
  const hasShownError = useRef(false);

  const syncUserWithBackend = useCallback(async (): Promise<boolean> => {
    if (!clerkUser || syncInProgress.current) return false;

    syncInProgress.current = true;

    try {
      const token = await getToken();

      if (!token) {
        console.error("[ClerkAuthSync] Failed to get Clerk token");
        return false;
      }

      const response = await api.post("/api/auth/clerk/sync", {
        clerkUserId: clerkUser.id,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const { user, token: backendToken } = response.data;

      if (user && backendToken) {
        storeAuthData(user, backendToken, true);

        identifyUser(user.id, {
          email: user.email,
          name: user.name,
          role: user.activeRole || user.roles?.[0],
        });

        // Don't store token in query cache - only user data
        const { token: _, ...userWithoutToken } = user;
        queryClient.setQueryData(["/api/user"], userWithoutToken);
        queryClient.invalidateQueries({ queryKey: ["/api/user"] });

        if (process.env.NODE_ENV === "development") {
          console.log("[ClerkAuthSync] User synced successfully:", user.id);
        }

        retryCount.current = 0;
        return true;
      }

      return false;
    } catch (error) {
      console.error("[ClerkAuthSync] Failed to sync user with backend:", error);
      return false;
    } finally {
      syncInProgress.current = false;
    }
  }, [clerkUser, getToken, queryClient]);

  const syncWithRetry = useCallback(async () => {
    const success = await syncUserWithBackend();

    if (!success && retryCount.current < MAX_RETRY_ATTEMPTS) {
      retryCount.current += 1;
      if (process.env.NODE_ENV === "development") {
        console.log(`[ClerkAuthSync] Retry attempt ${retryCount.current}/${MAX_RETRY_ATTEMPTS}`);
      }

      setTimeout(() => {
        hasSynced.current = false; // Allow retry
        syncWithRetry();
      }, RETRY_DELAY_MS * retryCount.current);
    } else if (!success && !hasShownError.current) {
      console.error("[ClerkAuthSync] Max retry attempts reached, sync failed");
      hasShownError.current = true;
      toast({
        variant: "destructive",
        title: t("auth:syncError", "Sync Error"),
        description: t("auth:syncErrorDescription", "Failed to sync your account. Please try refreshing the page or signing in again."),
      });
    }
  }, [syncUserWithBackend, toast, t]);

  useEffect(() => {
    if (!isLoaded || !isUserLoaded) return;

    if (isSignedIn && clerkUser && !hasSynced.current && !syncInProgress.current) {
      hasSynced.current = true;
      syncWithRetry();
    }

    if (!isSignedIn && hasSynced.current) {
      hasSynced.current = false;
      retryCount.current = 0;
      clearAuthData();
      resetUser();
      queryClient.setQueryData(["/api/user"], null);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    }
  }, [isLoaded, isUserLoaded, isSignedIn, clerkUser, syncWithRetry, queryClient]);

  return <>{children}</>;
}

export default ClerkAuthSync;
